<template>
  <div class="action-bar">
    <button
      type="button"
      class="action-bar__rail"
      :class="{ 'action-bar__rail--ghost': prevGhost }"
      @click="prevGhost || $emit('prev')"
    >
      <i class="fas fa-step-backward"></i>
      <span>Prev</span>
    </button>

    <div class="action-bar__middle">
      <button
        v-if="secondary"
        type="button"
        class="action-bar__btn action-bar__btn--secondary"
        @click="$emit('secondary-action')"
      >
        <i class="fas" :class="secondary.icon"></i>
        <span class="action-bar__label">{{ secondary.label }}</span>
      </button>

      <button
        v-if="primary"
        type="button"
        class="action-bar__btn action-bar__btn--primary"
        :class="['action-bar__btn--' + primary.variant, { 'action-bar__btn--inert': primary.inert }]"
        @click="onPrimaryClick"
      >
        <i class="fas" :class="primary.icon"></i>
        <span class="action-bar__label">{{ primary.label }}</span>
        <span v-if="countdown" class="action-bar__drain"></span>
      </button>
    </div>

    <!-- Ghosted whenever the primary is already Next. -->
    <button
      type="button"
      class="action-bar__rail"
      :class="{ 'action-bar__rail--ghost': nextGhost }"
      @click="nextGhost || $emit('next')"
    >
      <i class="fas fa-step-forward"></i>
      <span>Next</span>
    </button>
  </div>
</template>

<script>
export default {
  name: 'ActionBar',
  props: {
    prevGhost: {
      type: Boolean,
      default: false
    },
    nextGhost: {
      type: Boolean,
      default: false
    },
    // { icon: 'fa-redo', label: 'Retry' }
    secondary: {
      type: Object,
      default: null
    },
    // { icon: 'fa-check', label: 'Done', variant: 'green', inert: false }
    primary: {
      type: Object,
      default: null
    },
    countdown: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    // CSS makes inert buttons unclickable; this also guards programmatic clicks.
    onPrimaryClick () {
      if (this.primary && this.primary.inert) return
      this.$emit('primary-action')
    }
  }
}
</script>

<style scoped>
/* No global border-box in this app; set it or padding overflows the viewport. */
.action-bar {
  box-sizing: border-box;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  /* The safe-area inset adds to the 64px band; border-box height alone would
     leave 29px on a notched iPhone and clip the 48px pills. */
  height: calc(var(--bottom-nav-h, 64px) + env(safe-area-inset-bottom, 0px));
  max-width: 800px;
  margin: 0 auto;
  background: var(--chrome);
  border-top: 1px solid var(--line);
  padding: 0 8px env(safe-area-inset-bottom, 0px);
  display: flex;
  align-items: center;
  gap: 8px;
}

.action-bar__rail {
  box-sizing: border-box;
  flex: 0 0 40px;
  /* Override base.css button padding, or min-width:auto swells each rail to ~70px. */
  padding: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  height: var(--h-lg);
  border-radius: var(--r-control);
  color: var(--muted);
  gap: 3px;
  transition: color 0.15s ease, background-color 0.15s ease;
}

.action-bar__rail:hover { color: var(--text); }

.action-bar__rail i {
  font-size: 16px;
}

.action-bar__rail span {
  font-size: 10px;
  font-weight: 600;
}

.action-bar__rail--ghost {
  opacity: 0.25;
  pointer-events: none;
}

.action-bar__middle {
  flex: 1;
  min-width: 0;
  display: flex;
  gap: 8px;
}

.action-bar__btn {
  box-sizing: border-box;
  flex: 1;
  /* base.css padding would ellipsise "Reset" at 390px; min-width: 0 lets it shrink. */
  padding: 0 12px;
  min-width: 0;
  height: var(--h-lg);
  border-radius: var(--r-control);
  font-size: 15px;
  font-weight: 700;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  border: none;
  /* Stop iOS's grey tap flash fighting the press transform. */
  -webkit-tap-highlight-color: transparent;
  transition: transform 0.18s ease, filter 0.18s ease;
}

/* Zero the transition only while :active: a quick tap released before the
   0.15s ramp and looked ignored. Release stays eased. */
.action-bar__btn:active {
  transform: scale(0.94);
  filter: brightness(0.88);
  transition-duration: 0s;
}

.action-bar__label {
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}

/* Same fill as .btn-secondary; the variants below are the only other looks. */
.action-bar__btn--secondary,
.action-bar__btn--navy {
  background: var(--field);
  color: var(--text);
}

.action-bar__btn--gold {
  background: var(--accent);
  color: var(--accent-ink);
}

.action-bar__btn--green {
  background: var(--pass);
  color: var(--pass-ink);
}

.action-bar__btn--red {
  background: var(--fail);
  color: var(--fail-ink);
}

/* Non-interactive but full opacity, so it doesn't read as disabled. */
.action-bar__btn--inert {
  pointer-events: none;
}

.action-bar__btn--inert:active {
  transform: none;
  filter: none;
}

/* relative + hidden to host the drain bar; wider so the pair reads as
   primary-plus-alternative. */
.action-bar__btn--primary {
  position: relative;
  overflow: hidden;
  flex: 1.4;
}

/* A wide pill can't host a round ring, so the countdown drains as a bar. */
.action-bar__drain {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 3px;
  background: rgba(0, 0, 0, 0.35);
  transform-origin: left;
  animation: action-bar-drain var(--advance-duration, 2000ms) linear forwards;
}

@keyframes action-bar-drain {
  from { transform: scaleX(1); }
  to   { transform: scaleX(0); }
}
</style>
